/**
 * NativeVoiceFactory - Native Web Audio API Voice Factory
 *
 * Creates voice instances using native Web Audio API nodes (OscillatorNode, GainNode, etc.)
 * Replaces Tone.js synthesizers with native implementations.
 */
import { ADSREnvelope, ADSRSettings } from './ADSREnvelope';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters, FilterSettings, OscillatorSettings, PitchAutomationSettings } from '../../types/instruments';
import {
    createNESTriangleWave,
    createAliasedSquareWave,
    createAliasedSawtoothWave,
    createSteppedSawtoothWave,
    generateLFSRWhiteNoise,
    generateLFSRPinkNoise,
    generateLFSRBrownNoise,
    generateKickBuffer,
    generateSnareBuffer,
    generateHiHatBuffer,
    generateTomBuffer,
    generateCymbalBuffer,
    generateClapBuffer,
} from './ChipBuffer';
/**
 * Voice interface for native Web Audio API voices
 */
export interface Voice {
    /** The voice's gain node for volume control */
    gainNode: GainNode;
    /** Optional effects node (AudioWorkletNode) for WASM effects */
    effectsNode: AudioWorkletNode | null;
    /** Optional filter node for frequency filtering */
    filterNode: BiquadFilterNode | null;
    /** Trigger a note at a specific time */
    trigger: (note: string, time: number, duration: number) => void;
    /** Update voice parameters in real-time */
    updateParameters: (parameters: InstrumentParameters) => void;
    /** Dispose of the voice and clean up resources */
    dispose: () => void;
}
/**
 * Configuration for creating a voice
 */
export interface VoiceConfig {
    /** Native AudioContext */
    audioContext: AudioContext;
    /** Instrument type to create */
    instrumentType: InstrumentType;
    /** ADSR envelope settings */
    adsr?: ADSRSettings | undefined;
    /** Instrument parameters */
    parameters?: InstrumentParameters | undefined;
    /** Optional effects node to connect to */
    effectsNode?: AudioWorkletNode | null | undefined;
    /** Destination node (usually master gain or effects) */
    destination: AudioNode;
}
/**
 * DAC quantization node state for 4-bit quantization processing.
 * Inserted between oscillator output and ADSR envelope input when enabled.
 */
interface DACQuantizationState {
    processor: ScriptProcessorNode;
    bypassed: boolean;
}

/**
 * Create a 4-bit DAC quantization ScriptProcessorNode.
 * Applies the same quantization formula as apply4BitQuantization in ChipBuffer:
 * Math.round((sample + 1) * 7.5) → clamp [0, 15] → (clamped / 7.5) - 1.0
 *
 * @param audioContext - Native AudioContext
 * @returns DACQuantizationState with processor node and bypass flag
 */
function createDACQuantizationNode(audioContext: AudioContext): DACQuantizationState {
    // Use 256 buffer size for low latency
    const processor = audioContext.createScriptProcessor(256, 1, 1);
    const state: DACQuantizationState = { processor, bypassed: false };

    processor.onaudioprocess = (event: AudioProcessingEvent) => {
        const input = event.inputBuffer.getChannelData(0);
        const output = event.outputBuffer.getChannelData(0);
        if (state.bypassed) {
            // Pass through unmodified
            output.set(input);
            return;
        }
        for (let i = 0; i < input.length; i++) {
            const sample = input[i]!;
            const quantized = Math.round((sample + 1) * 7.5);
            const clamped = quantized < 0 ? 0 : quantized > 15 ? 15 : quantized;
            output[i] = (clamped / 7.5) - 1.0;
        }
    };

    return state;
}

/**
 * Default ADSR settings for different instrument types
 */
const DEFAULT_ADSR: ADSRSettings = {
    attack: 0.01,
    decay: 0.1,
    sustain: 0.7,
    release: 0.3,
};
/**
 * Create and configure a BiquadFilterNode based on filter settings
 *
 * @param audioContext - Native AudioContext
 * @param filterSettings - Filter configuration
 * @returns Configured BiquadFilterNode or null if no filter settings
 */
function createFilterNode(audioContext: AudioContext, filterSettings?: FilterSettings): BiquadFilterNode | null {
    if (!filterSettings) {
        return null;
    }
    const filter = audioContext.createBiquadFilter();
    // Set filter type (default to lowpass)
    filter.type = filterSettings.type || 'lowpass';
    // Set frequency (default to 20000 Hz for lowpass, 20 Hz for highpass, 1000 Hz for bandpass)
    if (filterSettings.frequency !== undefined) {
        // Clamp frequency to valid range (20 Hz - 20000 Hz)
        filter.frequency.value = Math.max(20, Math.min(20000, filterSettings.frequency));
    }
    else {
        // Set sensible defaults based on filter type
        switch (filter.type) {
            case 'lowpass':
                filter.frequency.value = 20000; // Fully open by default
                break;
            case 'highpass':
                filter.frequency.value = 20; // Fully open by default
                break;
            case 'bandpass':
                filter.frequency.value = 1000; // Middle frequency
                break;
        }
    }
    // Set resonance/Q (default to 1.0)
    if (filterSettings.resonance !== undefined) {
        filter.Q.value = filterSettings.resonance;
    }
    else {
        filter.Q.value = 1.0;
    }
    return filter;
}
/**
 * Update filter parameters in real-time
 *
 * @param filter - BiquadFilterNode to update
 * @param filterSettings - New filter settings
 */
function updateFilterParameters(filter: BiquadFilterNode, filterSettings: FilterSettings): void {
    // Update filter type if specified
    if (filterSettings.type !== undefined) {
        filter.type = filterSettings.type;
    }
    // Update frequency if specified (with clamping)
    if (filterSettings.frequency !== undefined) {
        const clampedFreq = Math.max(20, Math.min(20000, filterSettings.frequency));
        filter.frequency.setValueAtTime(clampedFreq, filter.context.currentTime);
    }
    // Update resonance/Q if specified
    if (filterSettings.resonance !== undefined) {
        filter.Q.setValueAtTime(filterSettings.resonance, filter.context.currentTime);
    }
}
/**
 * Update oscillator parameters in real-time for active oscillators
 *
 * @param oscillators - Array of active OscillatorNodes
 * @param audioContext - AudioContext for timing
 * @param oscillatorSettings - New oscillator settings
 */
function updateOscillatorParameters(oscillators: OscillatorNode[], audioContext: AudioContext, oscillatorSettings: OscillatorSettings): void {
    const currentTime = audioContext.currentTime;
    oscillators.forEach(oscillator => {
        // Update detune if specified
        if (oscillatorSettings.detune !== undefined) {
            oscillator.detune.setValueAtTime(oscillatorSettings.detune, currentTime);
        }
        // Note: oscillator type and frequency cannot be changed after creation
        // These changes will only apply to newly triggered notes
        // Frequency is determined by the note being played, not by parameters
    });
}
/**
 * Create a custom PeriodicWave for pulse waves with specified duty cycle
 * Uses Fourier series to generate pulse waveforms
 *
 * @param audioContext - Native AudioContext
 * @param dutyCycle - Duty cycle (0.0 to 1.0), e.g., 0.25 for 25% pulse
 * @returns PeriodicWave for use with OscillatorNode
 */
function createPulseWave(audioContext: AudioContext, dutyCycle: number): PeriodicWave {
    // Number of harmonics to generate (more = better quality but more CPU)
    const harmonics = 32;
    // Create arrays for real and imaginary components
    // Length must be harmonics + 1 (includes DC component at index 0)
    const real = new Float32Array(harmonics + 1);
    const imag = new Float32Array(harmonics + 1);
    // DC component (index 0) represents the average value
    // For a pulse wave, this is: 2 * dutyCycle - 1
    real[0] = 2 * dutyCycle - 1;
    imag[0] = 0;
    // Generate harmonics using Fourier series for pulse wave
    // Formula: a_n = (2/n*pi) * sin(n * pi * dutyCycle)
    for (let n = 1; n <= harmonics; n++) {
        const coefficient = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * dutyCycle);
        real[n] = coefficient;
        imag[n] = 0; // Pulse waves have no imaginary component
    }
    // Create and return the PeriodicWave
    return audioContext.createPeriodicWave(real, imag, { disableNormalization: false });
}
/**
 * Convert note name to frequency in Hz
 * Supports notes like 'C4', 'D#5', 'F3', etc.
 */
function noteToFrequency(note: string): number {
    const noteMap: Record<string, number> = {
        'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
        'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
        'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11,
    };
    // Parse note name and octave
    const match = note.match(/^([A-G][#b]?)(\d+)$/);
    if (!match) {
        return 261.63; // C4
    }
    const noteName = match[1];
    const octaveStr = match[2];
    if (!noteName || !octaveStr) {
        return 261.63; // C4
    }
    const octave = parseInt(octaveStr, 10);
    const noteIndex = noteMap[noteName];
    if (noteIndex === undefined) {
        return 261.63; // C4
    }
    // Calculate frequency using A4 = 440 Hz as reference
    // Formula: f = 440 * 2^((n - 69) / 12)
    // where n is the MIDI note number
    const midiNote = (octave + 1) * 12 + noteIndex;
    const frequency = 440 * Math.pow(2, (midiNote - 69) / 12);
    return frequency;
}
/**
 * Nodes created by applyPitchAutomation that need cleanup on dispose.
 */
interface PitchAutomationNodes {
    lfo?: OscillatorNode;
    lfoGain?: GainNode;
}

/**
 * Apply pitch automation (vibrato and/or pitch sweep) to an oscillator.
 *
 * - Vibrato: creates an LFO → GainNode → oscillator.detune chain.
 *   The gain starts at 0 and ramps to the configured depth after the delay.
 * - Pitch sweep: schedules a frequency ramp from noteFreq to the target frequency.
 *
 * Returns nodes that the caller must disconnect on dispose.
 */
function applyPitchAutomation(
    audioContext: AudioContext,
    oscillator: OscillatorNode,
    pitchAutomation: PitchAutomationSettings,
    triggerTime: number,
    noteFreq: number,
): PitchAutomationNodes {
    const nodes: PitchAutomationNodes = {};

    // Vibrato: LFO → GainNode → oscillator.detune
    if (pitchAutomation.vibrato) {
        const { rate, depth, delay } = pitchAutomation.vibrato;
        const lfo = audioContext.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = rate;

        const lfoGain = audioContext.createGain();
        lfoGain.gain.setValueAtTime(0, triggerTime);

        // Ramp to target depth after delay (50ms ramp)
        const rampStart = triggerTime + delay;
        lfoGain.gain.setValueAtTime(0, rampStart);
        lfoGain.gain.linearRampToValueAtTime(depth, rampStart + 0.05);

        lfo.connect(lfoGain);
        lfoGain.connect(oscillator.detune);
        lfo.start(triggerTime);

        nodes.lfo = lfo;
        nodes.lfoGain = lfoGain;
    }

    // Pitch sweep: frequency ramp from noteFreq to target
    if (pitchAutomation.pitchSweep) {
        const { target, duration, curve } = pitchAutomation.pitchSweep;
        const targetFreq = noteFreq * Math.pow(2, target / 12);
        oscillator.frequency.setValueAtTime(noteFreq, triggerTime);

        const sweepEnd = triggerTime + duration;
        if (curve === 'exponential') {
            // exponentialRamp requires target > 0
            oscillator.frequency.exponentialRampToValueAtTime(Math.max(targetFreq, 0.001), sweepEnd);
        } else {
            oscillator.frequency.linearRampToValueAtTime(targetFreq, sweepEnd);
        }
    }

    return nodes;
}

/**
 * Dispose pitch automation nodes (LFO + gain).
 */
function disposePitchAutomationNodes(nodes: PitchAutomationNodes): void {
    if (nodes.lfo) {
        try { nodes.lfo.stop(); nodes.lfo.disconnect(); } catch { /* ignore */ }
    }
    if (nodes.lfoGain) {
        try { nodes.lfoGain.disconnect(); } catch { /* ignore */ }
    }
}

/**
 * Create a pulse wave voice (pulse25, pulse50, pulse75).
 * Uses a PERSISTENT oscillator with a pre-created PeriodicWave — no per-note node allocation.
 */
function createPulseVoice(config: VoiceConfig): Voice {
    const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
    const dutyCycle = instrumentType === 'pulse25' ? 0.25 : instrumentType === 'pulse75' ? 0.75 : 0.50;
    const pulseWave = createPulseWave(audioContext, dutyCycle);
    const adsrSettings = adsr || DEFAULT_ADSR;

    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    const filterNode = createFilterNode(audioContext, parameters?.filter);

    if (filterNode) {
        if (effectsNode) { voiceGain.connect(filterNode); filterNode.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(filterNode); filterNode.connect(destination); }
    } else {
        if (effectsNode) { voiceGain.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(destination); }
    }

    const envelope = new ADSREnvelope(audioContext, voiceGain);

    // Persistent oscillator with pulse PeriodicWave
    const oscillator = audioContext.createOscillator();
    oscillator.setPeriodicWave(pulseWave);
    oscillator.frequency.value = 261.63;
    if (parameters?.oscillator?.detune) oscillator.detune.value = parameters.oscillator.detune;

    // DAC quantization: insert between oscillator and envelope when enabled
    let dacState: DACQuantizationState | null = null;
    if (parameters?.dacQuantization) {
        dacState = createDACQuantizationNode(audioContext);
        oscillator.connect(dacState.processor);
        dacState.processor.connect(envelope.getGainNode());
    } else {
        oscillator.connect(envelope.getGainNode());
    }
    oscillator.start();

    let pitchNodes: PitchAutomationNodes = {};

    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode,
        trigger(note: string, time: number, duration: number): void {
            disposePitchAutomationNodes(pitchNodes);
            const freq = noteToFrequency(note);
            oscillator.frequency.setValueAtTime(freq, time);
            if (parameters?.pitchAutomation) {
                pitchNodes = applyPitchAutomation(audioContext, oscillator, parameters.pitchAutomation, time, freq);
            }
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (filterNode && newParameters.filter) updateFilterParameters(filterNode, newParameters.filter);
            if (newParameters.oscillator?.detune !== undefined) {
                oscillator.detune.setValueAtTime(newParameters.oscillator.detune, audioContext.currentTime);
            }
            if (newParameters.adsr) Object.assign(adsrSettings, newParameters.adsr);
            // Handle DAC quantization toggle without voice recreation
            if (newParameters.dacQuantization !== undefined) {
                if (newParameters.dacQuantization && !dacState) {
                    dacState = createDACQuantizationNode(audioContext);
                    try { oscillator.disconnect(envelope.getGainNode()); } catch { /* ignore */ }
                    oscillator.connect(dacState.processor);
                    dacState.processor.connect(envelope.getGainNode());
                } else if (!newParameters.dacQuantization && dacState) {
                    try { oscillator.disconnect(dacState.processor); } catch { /* ignore */ }
                    try { dacState.processor.disconnect(); } catch { /* ignore */ }
                    dacState = null;
                    oscillator.connect(envelope.getGainNode());
                }
            }
            if (parameters) Object.assign(parameters, newParameters);
        },
        dispose(): void {
            disposePitchAutomationNodes(pitchNodes);
            try { oscillator.stop(); oscillator.disconnect(); } catch { /* ignore */ }
            if (dacState) { try { dacState.processor.disconnect(); } catch { /* ignore */ } }
            envelope.dispose();
            if (filterNode) { try { filterNode.disconnect(); } catch { /* ignore */ } }
            try { voiceGain.disconnect(); } catch { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Generate white noise buffer
 * White noise has equal energy at all frequencies
 */
function generateWhiteNoise(audioContext: AudioContext, duration: number = 2.0): AudioBuffer {
    const sampleRate = audioContext.sampleRate;
    const bufferSize = sampleRate * duration;
    const buffer = audioContext.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);
    // Generate random values between -1 and 1
    for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
    }
    return buffer;
}
/**
 * Create a noise voice (white, pink, brown).
 * Uses a PERSISTENT looping AudioBufferSourceNode — generated once, gated by ADSR.
 * Eliminates per-trigger buffer source allocation which causes GC pressure.
 */
function createNoiseVoice(config: VoiceConfig): Voice {
    const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
    // Use LFSR-based noise generators from ChipBuffer for authentic chip hardware noise
    const duration = 2.0;
    let noiseBuffer: AudioBuffer;
    switch (instrumentType) {
        case 'noise-pink': noiseBuffer = bufferFromFloat32(audioContext, generateLFSRPinkNoise(audioContext.sampleRate, duration)); break;
        case 'noise-brown': noiseBuffer = bufferFromFloat32(audioContext, generateLFSRBrownNoise(audioContext.sampleRate, duration)); break;
        default: noiseBuffer = bufferFromFloat32(audioContext, generateLFSRWhiteNoise(audioContext.sampleRate, duration)); break;
    }
    const adsrSettings = adsr || DEFAULT_ADSR;

    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.3;
    const filterNode = createFilterNode(audioContext, parameters?.filter);

    if (filterNode) {
        if (effectsNode) { voiceGain.connect(filterNode); filterNode.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(filterNode); filterNode.connect(destination); }
    } else {
        if (effectsNode) { voiceGain.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(destination); }
    }

    const envelope = new ADSREnvelope(audioContext, voiceGain);

    // Persistent looping noise source — runs continuously, ADSR gates the amplitude
    const noiseSource = audioContext.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    noiseSource.connect(envelope.getGainNode());
    noiseSource.start();

    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode,
        trigger(_note: string, time: number, duration: number): void {
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (filterNode && newParameters.filter) updateFilterParameters(filterNode, newParameters.filter);
            if (newParameters.adsr) Object.assign(adsrSettings, newParameters.adsr);
            if (parameters) Object.assign(parameters, newParameters);
        },
        dispose(): void {
            try { noiseSource.stop(); noiseSource.disconnect(); } catch { /* ignore */ }
            envelope.dispose();
            if (filterNode) { try { filterNode.disconnect(); } catch { /* ignore */ } }
            try { voiceGain.disconnect(); } catch { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Create an FM synthesis voice.
 * Uses PERSISTENT carrier + modulator oscillators — frequency updated on each trigger.
 * Modulator frequency tracks carrier frequency × ratio so timbre stays consistent across notes.
 */
function createFMVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, parameters, effectsNode, destination } = config;
    const adsrSettings = adsr || DEFAULT_ADSR;
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    const filterNode = createFilterNode(audioContext, parameters?.filter);
    // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    }
    else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(destination);
        }
    }
    // Persistent carrier + modulator — both start immediately, gated by ADSR
    const envelope = new ADSREnvelope(audioContext, voiceGain);

    const modulationGain = audioContext.createGain();
    modulationGain.gain.value = parameters?.modulation?.depth ?? 100;

    const modulator = audioContext.createOscillator();
    modulator.type = 'sine';
    modulator.frequency.value = 261.63 * (parameters?.modulation?.frequency ?? 2);

    const carrier = audioContext.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.value = 261.63;

    modulator.connect(modulationGain);
    modulationGain.connect(carrier.frequency);

    // DAC quantization: insert between carrier and envelope when enabled
    let dacState: DACQuantizationState | null = null;
    if (parameters?.dacQuantization) {
        dacState = createDACQuantizationNode(audioContext);
        carrier.connect(dacState.processor);
        dacState.processor.connect(envelope.getGainNode());
    } else {
        carrier.connect(envelope.getGainNode());
    }

    modulator.start();
    carrier.start();

    let pitchNodes: PitchAutomationNodes = {};

    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode,
        trigger(note: string, time: number, duration: number): void {
            disposePitchAutomationNodes(pitchNodes);
            const freq = noteToFrequency(note);
            const modRatio = parameters?.modulation?.frequency ?? 2;
            carrier.frequency.setValueAtTime(freq, time);
            modulator.frequency.setValueAtTime(freq * modRatio, time);
            if (parameters?.pitchAutomation) {
                pitchNodes = applyPitchAutomation(audioContext, carrier, parameters.pitchAutomation, time, freq);
            }
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (filterNode && newParameters.filter) updateFilterParameters(filterNode, newParameters.filter);
            if (newParameters.modulation?.depth !== undefined) {
                modulationGain.gain.setValueAtTime(newParameters.modulation.depth, audioContext.currentTime);
            }
            if (newParameters.modulation?.frequency !== undefined) {
                modulator.frequency.setValueAtTime(
                    carrier.frequency.value * newParameters.modulation.frequency,
                    audioContext.currentTime,
                );
            }
            if (newParameters.adsr) Object.assign(adsrSettings, newParameters.adsr);
            // Handle DAC quantization toggle without voice recreation
            if (newParameters.dacQuantization !== undefined) {
                if (newParameters.dacQuantization && !dacState) {
                    dacState = createDACQuantizationNode(audioContext);
                    try { carrier.disconnect(envelope.getGainNode()); } catch { /* ignore */ }
                    carrier.connect(dacState.processor);
                    dacState.processor.connect(envelope.getGainNode());
                } else if (!newParameters.dacQuantization && dacState) {
                    try { carrier.disconnect(dacState.processor); } catch { /* ignore */ }
                    try { dacState.processor.disconnect(); } catch { /* ignore */ }
                    dacState = null;
                    carrier.connect(envelope.getGainNode());
                }
            }
            if (parameters) Object.assign(parameters, newParameters);
        },
        dispose(): void {
            disposePitchAutomationNodes(pitchNodes);
            try { carrier.stop(); carrier.disconnect(); } catch { /* ignore */ }
            try { modulator.stop(); modulator.disconnect(); } catch { /* ignore */ }
            try { modulationGain.disconnect(); } catch { /* ignore */ }
            if (dacState) { try { dacState.processor.disconnect(); } catch { /* ignore */ } }
            envelope.dispose();
            if (filterNode) { try { filterNode.disconnect(); } catch { /* ignore */ } }
            try { voiceGain.disconnect(); } catch { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Create an AM synthesis voice.
 * Uses PERSISTENT carrier + modulator oscillators — no per-note node allocation.
 */
function createAMVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, parameters, effectsNode, destination } = config;
    const adsrSettings = adsr || DEFAULT_ADSR;

    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    const filterNode = createFilterNode(audioContext, parameters?.filter);

    if (filterNode) {
        if (effectsNode) { voiceGain.connect(filterNode); filterNode.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(filterNode); filterNode.connect(destination); }
    } else {
        if (effectsNode) { voiceGain.connect(effectsNode); effectsNode.connect(destination); }
        else { voiceGain.connect(destination); }
    }

    const envelope = new ADSREnvelope(audioContext, voiceGain);

    // Persistent AM chain: carrier → modulationGain → envelope
    //                       modulator → depthGain → modulationGain.gain
    const depth = parameters?.modulation?.depth ?? 0.5;
    const modFreq = parameters?.modulation?.frequency ?? 5;

    const carrier = audioContext.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.value = 261.63;

    const modulationGain = audioContext.createGain();
    modulationGain.gain.value = 1.0 - (depth / 2);

    const modulator = audioContext.createOscillator();
    modulator.type = 'sine';
    modulator.frequency.value = modFreq;

    const depthGain = audioContext.createGain();
    depthGain.gain.value = depth / 2;

    carrier.connect(modulationGain);

    // DAC quantization: insert between AM output (modulationGain) and envelope when enabled
    let dacState: DACQuantizationState | null = null;
    if (parameters?.dacQuantization) {
        dacState = createDACQuantizationNode(audioContext);
        modulationGain.connect(dacState.processor);
        dacState.processor.connect(envelope.getGainNode());
    } else {
        modulationGain.connect(envelope.getGainNode());
    }

    modulator.connect(depthGain);
    depthGain.connect(modulationGain.gain);

    carrier.start();
    modulator.start();

    let pitchNodes: PitchAutomationNodes = {};

    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode,
        trigger(note: string, time: number, duration: number): void {
            disposePitchAutomationNodes(pitchNodes);
            const freq = noteToFrequency(note);
            carrier.frequency.setValueAtTime(freq, time);
            if (parameters?.pitchAutomation) {
                pitchNodes = applyPitchAutomation(audioContext, carrier, parameters.pitchAutomation, time, freq);
            }
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (filterNode && newParameters.filter) updateFilterParameters(filterNode, newParameters.filter);
            const now = audioContext.currentTime;
            if (newParameters.modulation?.depth !== undefined) {
                const d = newParameters.modulation.depth;
                modulationGain.gain.setValueAtTime(1.0 - (d / 2), now);
                depthGain.gain.setValueAtTime(d / 2, now);
            }
            if (newParameters.modulation?.frequency !== undefined) {
                modulator.frequency.setValueAtTime(newParameters.modulation.frequency, now);
            }
            if (newParameters.adsr) Object.assign(adsrSettings, newParameters.adsr);
            // Handle DAC quantization toggle without voice recreation
            if (newParameters.dacQuantization !== undefined) {
                if (newParameters.dacQuantization && !dacState) {
                    dacState = createDACQuantizationNode(audioContext);
                    try { modulationGain.disconnect(envelope.getGainNode()); } catch { /* ignore */ }
                    modulationGain.connect(dacState.processor);
                    dacState.processor.connect(envelope.getGainNode());
                } else if (!newParameters.dacQuantization && dacState) {
                    try { modulationGain.disconnect(dacState.processor); } catch { /* ignore */ }
                    try { dacState.processor.disconnect(); } catch { /* ignore */ }
                    dacState = null;
                    modulationGain.connect(envelope.getGainNode());
                }
            }
            if (parameters) Object.assign(parameters, newParameters);
        },
        dispose(): void {
            disposePitchAutomationNodes(pitchNodes);
            try { carrier.stop(); carrier.disconnect(); } catch { /* ignore */ }
            try { modulator.stop(); modulator.disconnect(); } catch { /* ignore */ }
            try { modulationGain.disconnect(); depthGain.disconnect(); } catch { /* ignore */ }
            if (dacState) { try { dacState.processor.disconnect(); } catch { /* ignore */ } }
            envelope.dispose();
            if (filterNode) { try { filterNode.disconnect(); } catch { /* ignore */ } }
            try { voiceGain.disconnect(); } catch { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Create a basic oscillator voice (square, triangle, sawtooth, sine).
 *
 * Uses a PERSISTENT oscillator — created once, frequency updated on each trigger.
 * This eliminates per-note AudioNode allocation which causes GC-induced static.
 * Triangle type uses NES-accurate 16-step quantized PeriodicWave from ChipBuffer.
 */
function createOscillatorVoice(config: VoiceConfig): Voice {
    const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
    const adsrSettings = adsr || DEFAULT_ADSR;

    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;

    const filterNode = createFilterNode(audioContext, parameters?.filter);

    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        } else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    } else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        } else {
            voiceGain.connect(destination);
        }
    }

    const envelope = new ADSREnvelope(audioContext, voiceGain);

    // Persistent oscillator — runs continuously, gated by ADSR gain (starts at 0)
    const oscillator = audioContext.createOscillator();
    if (instrumentType === 'triangle') {
        // NES-accurate 16-step quantized triangle (buzzy/stepped, not smooth)
        oscillator.setPeriodicWave(createNESTriangleWave(audioContext));
    } else if (instrumentType === 'square') {
        // Aliased square wave — includes harmonics beyond Nyquist for authentic chip aliasing
        oscillator.setPeriodicWave(createAliasedSquareWave(audioContext));
    } else if (instrumentType === 'sawtooth') {
        // Aliased or stepped sawtooth depending on sawtoothMode parameter
        if (parameters?.sawtoothMode === 'stepped') {
            oscillator.setPeriodicWave(createSteppedSawtoothWave(audioContext));
        } else {
            oscillator.setPeriodicWave(createAliasedSawtoothWave(audioContext));
        }
    } else {
        oscillator.type = 'sine';
    }
    oscillator.frequency.value = 261.63; // C4 default, updated on each trigger
    if (parameters?.oscillator?.detune) {
        oscillator.detune.value = parameters.oscillator.detune;
    }
    // DAC quantization: insert between oscillator and envelope when enabled
    let dacState: DACQuantizationState | null = null;
    if (parameters?.dacQuantization) {
        dacState = createDACQuantizationNode(audioContext);
        oscillator.connect(dacState.processor);
        dacState.processor.connect(envelope.getGainNode());
    } else {
        oscillator.connect(envelope.getGainNode());
    }
    oscillator.start(); // start immediately, ADSR keeps gain at 0 between notes

    let pitchNodes: PitchAutomationNodes = {};

    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode,
        trigger(note: string, time: number, duration: number): void {
            disposePitchAutomationNodes(pitchNodes);
            const freq = noteToFrequency(note);
            oscillator.frequency.setValueAtTime(freq, time);
            if (parameters?.pitchAutomation) {
                pitchNodes = applyPitchAutomation(audioContext, oscillator, parameters.pitchAutomation, time, freq);
            }
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (filterNode && newParameters.filter) {
                updateFilterParameters(filterNode, newParameters.filter);
            }
            if (newParameters.oscillator?.detune !== undefined) {
                oscillator.detune.setValueAtTime(newParameters.oscillator.detune, audioContext.currentTime);
            }
            if (newParameters.adsr) {
                Object.assign(adsrSettings, newParameters.adsr);
            }
            // Handle DAC quantization toggle without voice recreation
            if (newParameters.dacQuantization !== undefined) {
                if (newParameters.dacQuantization && !dacState) {
                    // Enable: insert quantization node
                    dacState = createDACQuantizationNode(audioContext);
                    try { oscillator.disconnect(envelope.getGainNode()); } catch { /* ignore */ }
                    oscillator.connect(dacState.processor);
                    dacState.processor.connect(envelope.getGainNode());
                } else if (!newParameters.dacQuantization && dacState) {
                    // Disable: remove quantization node
                    try { oscillator.disconnect(dacState.processor); } catch { /* ignore */ }
                    try { dacState.processor.disconnect(); } catch { /* ignore */ }
                    dacState = null;
                    oscillator.connect(envelope.getGainNode());
                }
            }
            if (parameters) {
                Object.assign(parameters, newParameters);
            }
        },
        dispose(): void {
            disposePitchAutomationNodes(pitchNodes);
            try { oscillator.stop(); oscillator.disconnect(); } catch { /* ignore */ }
            if (dacState) { try { dacState.processor.disconnect(); } catch { /* ignore */ } }
            envelope.dispose();
            if (filterNode) { try { filterNode.disconnect(); } catch { /* ignore */ } }
            try { voiceGain.disconnect(); } catch { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Shared helper: create a drum voice backed by a pre-rendered AudioBuffer.
 * The buffer has amplitude envelope baked in (chip-accurate synthesis via ChipBuffer).
 * Each trigger creates only ONE cheap AudioBufferSourceNode instead of 5-12 live nodes.
 */
function createPrerenderedDrumVoice(
    audioContext: AudioContext,
    buffer: AudioBuffer,
    effectsNode: AudioWorkletNode | null,
    destination: AudioNode,
    gainLevel: number = 0.8,
    regenerateBuffer?: (tuning: import('../../types/instruments').DrumTuningSettings | undefined) => Float32Array,
): Voice {
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = gainLevel;

    if (effectsNode) {
        voiceGain.connect(effectsNode);
        effectsNode.connect(destination);
    } else {
        voiceGain.connect(destination);
    }

    let currentBuffer = buffer;

    return {
        gainNode: voiceGain,
        effectsNode,
        filterNode: null,
        trigger(_note: string, time: number, _duration: number): void {
            const src = audioContext.createBufferSource();
            src.buffer = currentBuffer;
            src.connect(voiceGain);
            src.start(time);
            src.stop(time + currentBuffer.duration + 0.05);
            src.onended = () => { try { src.disconnect(); } catch { /* ignore */ } };
        },
        updateParameters(newParameters: InstrumentParameters): void {
            if (regenerateBuffer && newParameters.drumTuning !== undefined) {
                try {
                    currentBuffer = bufferFromFloat32(audioContext, regenerateBuffer(newParameters.drumTuning));
                } catch { /* keep existing buffer on failure */ }
            }
        },
        dispose(): void { try { voiceGain.disconnect(); } catch { /* ignore */ } },
    };
}

function bufferFromFloat32(audioContext: AudioContext, data: Float32Array): AudioBuffer {
    const buf = audioContext.createBuffer(1, data.length, audioContext.sampleRate);
    buf.getChannelData(0).set(data);
    return buf;
}

/**
 * Create a kick drum voice — NES-accurate: sine pitch-sweep + LFSR click transient
 */
function createKickDrumVoice(config: VoiceConfig): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateKickBuffer(audioContext.sampleRate, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.8,
        (tuning) => generateKickBuffer(audioContext.sampleRate, tuning),
    );
}
/**
 * Create a snare drum voice
 * Snare drum: noise + resonant filter + short envelope
 */
function createSnareDrumVoice(config: VoiceConfig): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateSnareBuffer(audioContext.sampleRate, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.65,
        (tuning) => generateSnareBuffer(audioContext.sampleRate, tuning),
    );
}
/**
 * Create a hi-hat voice — NES short-mode LFSR (metallic texture, period=93 samples)
 */
function createHiHatVoice(config: VoiceConfig, isOpen: boolean = false): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateHiHatBuffer(audioContext.sampleRate, isOpen, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.45,
        (tuning) => generateHiHatBuffer(audioContext.sampleRate, isOpen, tuning),
    );
}
/**
 * Create a tom drum voice — sine wave with pitch sweep, 4-bit quantization
 */
function createTomDrumVoice(config: VoiceConfig, pitch: 'low' | 'mid' | 'high' = 'mid'): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateTomBuffer(audioContext.sampleRate, pitch, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.7,
        (tuning) => generateTomBuffer(audioContext.sampleRate, pitch, tuning),
    );
}
/**
 * Create a cymbal voice — LFSR-based metallic noise, highpassed
 */
function createCymbalVoice(config: VoiceConfig, type: 'crash' | 'ride' = 'crash'): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateCymbalBuffer(audioContext.sampleRate, type, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.5,
        (tuning) => generateCymbalBuffer(audioContext.sampleRate, type, tuning),
    );
}
/**
 * Create a clap voice — 4x LFSR bursts at staggered offsets
 */
function createClapVoice(config: VoiceConfig): Voice {
    const { audioContext, parameters, effectsNode, destination } = config;
    return createPrerenderedDrumVoice(
        audioContext,
        bufferFromFloat32(audioContext, generateClapBuffer(audioContext.sampleRate, parameters?.drumTuning)),
        effectsNode ?? null, destination, 0.6,
        (tuning) => generateClapBuffer(audioContext.sampleRate, tuning),
    );
}
/**
 * Create a pluck voice using Karplus-Strong synthesis
 * Karplus-Strong: DelayNode + BiquadFilterNode feedback loop initialized with noise burst
 */
function createPluckVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, parameters, effectsNode, destination } = config;
    // Pluck has a natural decay, so we use a simple envelope
    const adsrSettings = adsr || {
        attack: 0.001,
        decay: 0.1,
        sustain: 0.3,
        release: 0.5,
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.6;
    // Create filter node if filter settings are provided
    const filterNode = createFilterNode(audioContext, parameters?.filter);
    // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    }
    else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(destination);
        }
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active nodes for cleanup
    let activeNoiseSources: AudioBufferSourceNode[] = [];
    let activeDelays: DelayNode[] = [];
    let activeFilters: BiquadFilterNode[] = [];
    let activeGains: GainNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: filterNode,
        trigger(note: string, time: number, duration: number): void {
            const frequency = noteToFrequency(note);
            // Karplus-Strong algorithm:
            // 1. Create a delay line with delay time = 1/frequency
            // 2. Initialize with a short noise burst
            // 3. Feed the output back through a lowpass filter to the input
            // 4. The feedback creates a decaying pitched sound
            // Calculate delay time (period of the note)
            const delayTime = 1 / frequency;
            // Create delay node
            const delay = audioContext.createDelay(5.0); // Max delay of 5 seconds
            delay.delayTime.value = delayTime;
            // Create feedback filter (lowpass to simulate string damping)
            const feedbackFilter = audioContext.createBiquadFilter();
            feedbackFilter.type = 'lowpass';
            feedbackFilter.frequency.value = frequency * 4; // Filter cutoff above fundamental
            feedbackFilter.Q.value = 1;
            // Create feedback gain (controls decay rate, must be < 1.0 for stability)
            const feedbackGain = audioContext.createGain();
            feedbackGain.gain.value = 0.95; // High feedback for longer sustain
            // Create the feedback loop: delay -> filter -> feedbackGain -> delay
            delay.connect(feedbackFilter);
            feedbackFilter.connect(feedbackGain);
            feedbackGain.connect(delay);
            // Connect delay output to envelope
            delay.connect(envelope.getGainNode());
            // Create noise burst to initialize the delay line
            const noiseDuration = 0.01; // Very short burst
            const noiseBuffer = generateWhiteNoise(audioContext, noiseDuration);
            const noiseSource = audioContext.createBufferSource();
            noiseSource.buffer = noiseBuffer;
            // Connect noise to delay line to start the oscillation
            noiseSource.connect(delay);
            // Start noise burst at scheduled time
            noiseSource.start(time);
            noiseSource.stop(time + noiseDuration);
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
            // Store for cleanup
            activeNoiseSources.push(noiseSource);
            activeDelays.push(delay);
            activeFilters.push(feedbackFilter);
            activeGains.push(feedbackGain);
            // Schedule cleanup after note ends
            const stopTime = time + duration + adsrSettings.release + 0.5;
            // Gradually reduce feedback to stop oscillation
            feedbackGain.gain.setValueAtTime(0.95, time + duration);
            feedbackGain.gain.linearRampToValueAtTime(0, stopTime);
            // Clean up after delay stops
            setTimeout(() => {
                try {
                    noiseSource.disconnect();
                    delay.disconnect();
                    feedbackFilter.disconnect();
                    feedbackGain.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-disconnected nodes
                }
                activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
                activeDelays = activeDelays.filter(d => d !== delay);
                activeFilters = activeFilters.filter(f => f !== feedbackFilter);
                activeGains = activeGains.filter(g => g !== feedbackGain);
            }, (stopTime - audioContext.currentTime) * 1000 + 100);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            // Update filter parameters in real-time
            if (filterNode && newParameters.filter) {
                updateFilterParameters(filterNode, newParameters.filter);
            }
            // Update ADSR settings for next trigger
            if (newParameters.adsr) {
                Object.assign(adsrSettings, newParameters.adsr);
            }
            // Store all parameters for next trigger
            if (parameters) {
                Object.assign(parameters, newParameters);
            }
        },
        dispose(): void {
            // Stop all active noise sources
            activeNoiseSources.forEach(src => {
                try {
                    src.stop();
                    src.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            // Disconnect all nodes
            activeDelays.forEach(d => {
                try {
                    d.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeFilters.forEach(f => {
                try {
                    f.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeGains.forEach(g => {
                try {
                    g.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeNoiseSources = [];
            activeDelays = [];
            activeFilters = [];
            activeGains = [];
            // Dispose envelope
            envelope.dispose();
            // Disconnect filter if present
            if (filterNode) {
                try {
                    filterNode.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-disconnected nodes
                }
            }
            // Disconnect voice gain
            try {
                voiceGain.disconnect();
            }
            catch (e) {
                // Ignore errors from already-disconnected nodes
            }
        },
    };
    return voice;
}
/**
 * Create a metal voice using multiple detuned oscillators
 * Metal synthesis: 5-7 detuned oscillators with inharmonic frequencies for metallic percussive sound
 */
function createMetalVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, parameters, effectsNode, destination } = config;
    // Metal has a short, percussive envelope
    const adsrSettings = adsr || {
        attack: 0.001,
        decay: 0.3,
        sustain: 0.0,
        release: 0.2,
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    // Create filter node if filter settings are provided
    const filterNode = createFilterNode(audioContext, parameters?.filter);
    // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    }
    else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(destination);
        }
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active oscillators for cleanup
    let activeOscillators: OscillatorNode[] = [];
    let activeGains: GainNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: filterNode,
        trigger(note: string, time: number, duration: number): void {
            const frequency = noteToFrequency(note);
            // Metal synthesis: multiple detuned oscillators with inharmonic ratios
            // These ratios create a metallic, bell-like timbre
            const inharmonicRatios = [1.0, 2.13, 3.17, 4.42, 5.63, 6.89, 8.12];
            // Create oscillators for each inharmonic partial
            inharmonicRatios.forEach((ratio, index) => {
                const oscillator = audioContext.createOscillator();
                oscillator.type = 'sine'; // Use sine waves for clean partials
                oscillator.frequency.value = frequency * ratio;
                // Apply slight random detuning for more complex sound
                const randomDetune = (Math.random() - 0.5) * 10; // ±5 cents
                oscillator.detune.value = randomDetune;
                // Create gain for this oscillator (higher partials are quieter)
                const oscGain = audioContext.createGain();
                const amplitude = 1.0 / (index + 1); // Decrease amplitude for higher partials
                oscGain.gain.value = amplitude / inharmonicRatios.length;
                // Connect: oscillator -> gain -> envelope
                oscillator.connect(oscGain);
                oscGain.connect(envelope.getGainNode());
                // Start oscillator at scheduled time
                oscillator.start(time);
                // Stop oscillator after note duration + release
                const stopTime = time + duration + adsrSettings.release + 0.1;
                oscillator.stop(stopTime);
                // Store for cleanup
                activeOscillators.push(oscillator);
                activeGains.push(oscGain);
                // Clean up after oscillator stops
                oscillator.onended = () => {
                    try {
                        oscillator.disconnect();
                        oscGain.disconnect();
                    }
                    catch (e) {
                        // Ignore errors from already-disconnected oscillators
                    }
                    activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
                    activeGains = activeGains.filter(g => g !== oscGain);
                };
            });
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            // Update filter parameters in real-time
            if (filterNode && newParameters.filter) {
                updateFilterParameters(filterNode, newParameters.filter);
            }
            // Update oscillator parameters for active oscillators
            if (newParameters.oscillator) {
                updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
            }
            // Update ADSR settings for next trigger
            if (newParameters.adsr) {
                Object.assign(adsrSettings, newParameters.adsr);
            }
            // Store all parameters for next trigger
            if (parameters) {
                Object.assign(parameters, newParameters);
            }
        },
        dispose(): void {
            // Stop all active oscillators
            activeOscillators.forEach(oscillator => {
                try {
                    oscillator.stop();
                    oscillator.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-stopped oscillators
                }
            });
            activeOscillators = [];
            // Disconnect all gains
            activeGains.forEach(gain => {
                try {
                    gain.disconnect();
                }
                catch (e) {
                    // Ignore errors
                }
            });
            activeGains = [];
            // Dispose envelope
            envelope.dispose();
            // Disconnect filter if present
            if (filterNode) {
                try {
                    filterNode.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-disconnected nodes
                }
            }
            // Disconnect voice gain
            try {
                voiceGain.disconnect();
            }
            catch (e) {
                // Ignore errors from already-disconnected nodes
            }
        },
    };
    return voice;
}
/**
 * Create a pad voice with long ADSR envelope and rich oscillator combinations
 * Pad instruments have slow attack and long release for atmospheric sounds
 */
function createPadVoice(config: VoiceConfig): Voice {
    const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
    // Pad instruments have long attack and release for atmospheric sounds
    const adsrSettings = adsr || {
        attack: 0.8, // Slow fade-in
        decay: 0.3,
        sustain: 0.7,
        release: 1.5, // Long fade-out
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.4; // Lower volume for pads (they're layered)
    // Create filter node if filter settings are provided
    const filterNode = createFilterNode(audioContext, parameters?.filter);
    // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    }
    else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(destination);
        }
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active oscillators for cleanup
    let activeOscillators: OscillatorNode[] = [];
    let activeGains: GainNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: filterNode,
        trigger(note: string, time: number, duration: number): void {
            const frequency = noteToFrequency(note);
            // Create rich pad sound with multiple detuned oscillators
            // Different pad types use different oscillator combinations
            let oscillatorConfigs: Array<{
                type: OscillatorType;
                detune: number;
                gain: number;
            }> = [];
            switch (instrumentType) {
                case 'pad-choir':
                    // Choir-like sound: multiple sawtooth waves with slight detuning
                    oscillatorConfigs = [
                        { type: 'sawtooth', detune: -10, gain: 0.25 },
                        { type: 'sawtooth', detune: -5, gain: 0.25 },
                        { type: 'sawtooth', detune: 5, gain: 0.25 },
                        { type: 'sawtooth', detune: 10, gain: 0.25 },
                    ];
                    break;
                case 'pad-sweep':
                    // Sweeping sound: triangle and sine waves with wider detuning
                    oscillatorConfigs = [
                        { type: 'triangle', detune: -15, gain: 0.3 },
                        { type: 'sine', detune: 0, gain: 0.4 },
                        { type: 'triangle', detune: 15, gain: 0.3 },
                    ];
                    break;
                case 'pad-drone':
                    // Drone sound: square waves with harmonic detuning
                    oscillatorConfigs = [
                        { type: 'square', detune: 0, gain: 0.3 },
                        { type: 'square', detune: 1200, gain: 0.2 }, // +1 octave
                        { type: 'square', detune: 1900, gain: 0.15 }, // +perfect fifth above octave
                    ];
                    break;
                default:
                    // Default pad: sawtooth waves
                    oscillatorConfigs = [
                        { type: 'sawtooth', detune: -7, gain: 0.33 },
                        { type: 'sawtooth', detune: 0, gain: 0.34 },
                        { type: 'sawtooth', detune: 7, gain: 0.33 },
                    ];
            }
            // Create oscillators based on configuration
            oscillatorConfigs.forEach(config => {
                const oscillator = audioContext.createOscillator();
                oscillator.type = config.type;
                oscillator.frequency.value = frequency;
                oscillator.detune.value = config.detune;
                // Apply additional detune from parameters if specified
                if (parameters?.oscillator?.detune) {
                    oscillator.detune.value += parameters.oscillator.detune;
                }
                // Create gain for this oscillator
                const oscGain = audioContext.createGain();
                oscGain.gain.value = config.gain;
                // Connect: oscillator -> gain -> envelope
                oscillator.connect(oscGain);
                oscGain.connect(envelope.getGainNode());
                // Start oscillator at scheduled time
                oscillator.start(time);
                // Stop oscillator after note duration + release
                const stopTime = time + duration + adsrSettings.release + 0.1;
                oscillator.stop(stopTime);
                // Store for cleanup
                activeOscillators.push(oscillator);
                activeGains.push(oscGain);
                // Clean up after oscillator stops
                oscillator.onended = () => {
                    try {
                        oscillator.disconnect();
                        oscGain.disconnect();
                    }
                    catch (e) {
                        // Ignore errors from already-disconnected oscillators
                    }
                    activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
                    activeGains = activeGains.filter(g => g !== oscGain);
                };
            });
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            // Update filter parameters in real-time
            if (filterNode && newParameters.filter) {
                updateFilterParameters(filterNode, newParameters.filter);
            }
            // Update oscillator parameters for active oscillators
            if (newParameters.oscillator) {
                updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
            }
            // Update ADSR settings for next trigger
            if (newParameters.adsr) {
                Object.assign(adsrSettings, newParameters.adsr);
            }
            // Store all parameters for next trigger
            if (parameters) {
                Object.assign(parameters, newParameters);
            }
        },
        dispose(): void {
            // Stop all active oscillators
            activeOscillators.forEach(oscillator => {
                try {
                    oscillator.stop();
                    oscillator.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-stopped oscillators
                }
            });
            activeOscillators = [];
            // Disconnect all gains
            activeGains.forEach(gain => {
                try {
                    gain.disconnect();
                }
                catch (e) {
                    // Ignore errors
                }
            });
            activeGains = [];
            // Dispose envelope
            envelope.dispose();
            // Disconnect filter if present
            if (filterNode) {
                try {
                    filterNode.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-disconnected nodes
                }
            }
            // Disconnect voice gain
            try {
                voiceGain.disconnect();
            }
            catch (e) {
                // Ignore errors from already-disconnected nodes
            }
        },
    };
    return voice;
}
/**
 * Create an arp (arpeggiator) voice with short ADSR envelope for plucky sounds
 * Arp instruments have fast attack and short release for rhythmic patterns
 */
function createArpVoice(config: VoiceConfig): Voice {
    const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
    // Arp instruments have short, plucky envelopes
    const adsrSettings = adsr || {
        attack: 0.01, // Fast attack
        decay: 0.1,
        sustain: 0.3,
        release: 0.1, // Short release
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    // Create filter node if filter settings are provided
    const filterNode = createFilterNode(audioContext, parameters?.filter);
    // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
    if (filterNode) {
        if (effectsNode) {
            voiceGain.connect(filterNode);
            filterNode.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(filterNode);
            filterNode.connect(destination);
        }
    }
    else {
        if (effectsNode) {
            voiceGain.connect(effectsNode);
            effectsNode.connect(destination);
        }
        else {
            voiceGain.connect(destination);
        }
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active oscillators for cleanup
    let activeOscillators: OscillatorNode[] = [];
    let activeGains: GainNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: filterNode,
        trigger(note: string, time: number, duration: number): void {
            const frequency = noteToFrequency(note);
            // Create arp sound based on type
            // Different arp types use different oscillator configurations
            let oscillatorConfigs: Array<{
                type: OscillatorType;
                detune: number;
                gain: number;
            }> = [];
            switch (instrumentType) {
                case 'arp-fast':
                    // Fast arp: bright square wave with slight detuning for width
                    oscillatorConfigs = [
                        { type: 'square', detune: -5, gain: 0.5 },
                        { type: 'square', detune: 5, gain: 0.5 },
                    ];
                    break;
                case 'arp-slow':
                    // Slow arp: warmer sawtooth with subtle detuning
                    oscillatorConfigs = [
                        { type: 'sawtooth', detune: -3, gain: 0.5 },
                        { type: 'sawtooth', detune: 3, gain: 0.5 },
                    ];
                    break;
                case 'arp-random':
                    // Random arp: triangle wave with wider detuning for unpredictability
                    oscillatorConfigs = [
                        { type: 'triangle', detune: -10, gain: 0.4 },
                        { type: 'triangle', detune: 0, gain: 0.3 },
                        { type: 'triangle', detune: 10, gain: 0.3 },
                    ];
                    break;
                case 'arp-chord':
                    // Chord arp: multiple oscillators tuned to chord intervals
                    oscillatorConfigs = [
                        { type: 'sawtooth', detune: 0, gain: 0.35 }, // Root
                        { type: 'sawtooth', detune: 400, gain: 0.3 }, // Major third
                        { type: 'sawtooth', detune: 700, gain: 0.35 }, // Perfect fifth
                    ];
                    break;
                default:
                    // Default arp: square wave
                    oscillatorConfigs = [
                        { type: 'square', detune: 0, gain: 1.0 },
                    ];
            }
            // Create oscillators based on configuration
            oscillatorConfigs.forEach(config => {
                const oscillator = audioContext.createOscillator();
                oscillator.type = config.type;
                oscillator.frequency.value = frequency;
                oscillator.detune.value = config.detune;
                // Apply additional detune from parameters if specified
                if (parameters?.oscillator?.detune) {
                    oscillator.detune.value += parameters.oscillator.detune;
                }
                // Create gain for this oscillator
                const oscGain = audioContext.createGain();
                oscGain.gain.value = config.gain;
                // Connect: oscillator -> gain -> envelope
                oscillator.connect(oscGain);
                oscGain.connect(envelope.getGainNode());
                // Start oscillator at scheduled time
                oscillator.start(time);
                // Stop oscillator after note duration + release
                const stopTime = time + duration + adsrSettings.release + 0.1;
                oscillator.stop(stopTime);
                // Store for cleanup
                activeOscillators.push(oscillator);
                activeGains.push(oscGain);
                // Clean up after oscillator stops
                oscillator.onended = () => {
                    try {
                        oscillator.disconnect();
                        oscGain.disconnect();
                    }
                    catch (e) {
                        // Ignore errors from already-disconnected oscillators
                    }
                    activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
                    activeGains = activeGains.filter(g => g !== oscGain);
                };
            });
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            // Update filter parameters in real-time
            if (filterNode && newParameters.filter) {
                updateFilterParameters(filterNode, newParameters.filter);
            }
            // Update oscillator parameters for active oscillators
            if (newParameters.oscillator) {
                updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
            }
            // Update ADSR settings for next trigger
            if (newParameters.adsr) {
                Object.assign(adsrSettings, newParameters.adsr);
            }
            // Store all parameters for next trigger
            if (parameters) {
                Object.assign(parameters, newParameters);
            }
        },
        dispose(): void {
            // Stop all active oscillators
            activeOscillators.forEach(oscillator => {
                try {
                    oscillator.stop();
                    oscillator.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-stopped oscillators
                }
            });
            activeOscillators = [];
            // Disconnect all gains
            activeGains.forEach(gain => {
                try {
                    gain.disconnect();
                }
                catch (e) {
                    // Ignore errors
                }
            });
            activeGains = [];
            // Dispose envelope
            envelope.dispose();
            // Disconnect filter if present
            if (filterNode) {
                try {
                    filterNode.disconnect();
                }
                catch (e) {
                    // Ignore errors from already-disconnected nodes
                }
            }
            // Disconnect voice gain
            try {
                voiceGain.disconnect();
            }
            catch (e) {
                // Ignore errors from already-disconnected nodes
            }
        },
    };
    return voice;
}
/**
 * Create a laser SFX voice
 * Laser: frequency sweep + oscillator with parameter automation
 */
function createLaserVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, effectsNode, destination } = config;
    // Laser has a very short envelope
    const adsrSettings = adsr || {
        attack: 0.001,
        decay: 0.15,
        sustain: 0.0,
        release: 0.05,
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    // Connect to effects or destination
    if (effectsNode) {
        voiceGain.connect(effectsNode);
        effectsNode.connect(destination);
    }
    else {
        voiceGain.connect(destination);
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active nodes for cleanup
    let activeOscillators: OscillatorNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: null,
        trigger(_note: string, time: number, duration: number): void {
            // Laser sound: frequency sweep from high to low
            // Create oscillator (square or sawtooth for laser character)
            const oscillator = audioContext.createOscillator();
            oscillator.type = 'sawtooth';
            // Frequency sweep parameters
            const startFreq = 1200; // Start high
            const endFreq = 200; // End low
            const sweepDuration = Math.min(duration, 0.2); // Sweep duration
            // Apply frequency sweep using exponential ramp
            oscillator.frequency.setValueAtTime(startFreq, time);
            oscillator.frequency.exponentialRampToValueAtTime(endFreq, time + sweepDuration);
            // Connect oscillator through envelope
            oscillator.connect(envelope.getGainNode());
            // Start oscillator
            oscillator.start(time);
            // Stop after duration + release
            const stopTime = time + duration + adsrSettings.release + 0.1;
            oscillator.stop(stopTime);
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
            // Store for cleanup
            activeOscillators.push(oscillator);
            // Clean up after oscillator stops
            oscillator.onended = () => {
                try {
                    oscillator.disconnect();
                }
                catch (e) {
                    // Ignore errors
                }
                activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
            };
        },
        updateParameters(newParameters: InstrumentParameters): void {
            Object.assign({}, newParameters);
        },
        dispose(): void {
            activeOscillators.forEach(osc => {
                try {
                    osc.stop();
                    osc.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeOscillators = [];
            envelope.dispose();
            try {
                voiceGain.disconnect();
            }
            catch (e) { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Create an explosion SFX voice
 * Explosion: noise + filter sweep + envelope with parameter automation
 */
function createExplosionVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, effectsNode, destination } = config;
    // Explosion has a short, punchy envelope with quick decay
    const adsrSettings = adsr || {
        attack: 0.001,
        decay: 0.3,
        sustain: 0.0,
        release: 0.2,
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.7;
    // Connect to effects or destination
    if (effectsNode) {
        voiceGain.connect(effectsNode);
        effectsNode.connect(destination);
    }
    else {
        voiceGain.connect(destination);
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active nodes for cleanup
    let activeNoiseSources: AudioBufferSourceNode[] = [];
    let activeFilters: BiquadFilterNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: null,
        trigger(_note: string, time: number, duration: number): void {
            // Explosion sound: noise with filter sweep from high to low
            // Create noise source
            const noiseBuffer = generateWhiteNoise(audioContext, 0.5);
            const noiseSource = audioContext.createBufferSource();
            noiseSource.buffer = noiseBuffer;
            // Create lowpass filter for sweep effect
            const filter = audioContext.createBiquadFilter();
            filter.type = 'lowpass';
            // Filter sweep parameters (high to low for explosion effect)
            const startFreq = 8000; // Start with bright sound
            const endFreq = 100; // End with deep rumble
            const sweepDuration = Math.min(duration, 0.4);
            // Apply filter frequency sweep
            filter.frequency.setValueAtTime(startFreq, time);
            filter.frequency.exponentialRampToValueAtTime(endFreq, time + sweepDuration);
            filter.Q.value = 1;
            // Connect: noise -> filter -> envelope
            noiseSource.connect(filter);
            filter.connect(envelope.getGainNode());
            // Start noise
            noiseSource.start(time);
            // Stop after duration + release
            const stopTime = time + duration + adsrSettings.release + 0.1;
            noiseSource.stop(stopTime);
            // Trigger envelope
            envelope.trigger(time, duration, adsrSettings);
            // Store for cleanup
            activeNoiseSources.push(noiseSource);
            activeFilters.push(filter);
            // Clean up after noise stops
            noiseSource.onended = () => {
                try {
                    noiseSource.disconnect();
                    filter.disconnect();
                }
                catch (e) {
                    // Ignore errors
                }
                activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
                activeFilters = activeFilters.filter(f => f !== filter);
            };
        },
        updateParameters(newParameters: InstrumentParameters): void {
            Object.assign({}, newParameters);
        },
        dispose(): void {
            activeNoiseSources.forEach(src => {
                try {
                    src.stop();
                    src.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeFilters.forEach(f => {
                try {
                    f.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeNoiseSources = [];
            activeFilters = [];
            envelope.dispose();
            try {
                voiceGain.disconnect();
            }
            catch (e) { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Create a coin SFX voice
 * Coin: arpeggio of frequencies with parameter automation
 */
function createCoinVoice(config: VoiceConfig): Voice {
    const { audioContext, adsr, effectsNode, destination } = config;
    // Coin has a short, bright envelope
    const adsrSettings = adsr || {
        attack: 0.001,
        decay: 0.15,
        sustain: 0.0,
        release: 0.1,
    };
    // Create voice gain node
    const voiceGain = audioContext.createGain();
    voiceGain.gain.value = 0.5;
    // Connect to effects or destination
    if (effectsNode) {
        voiceGain.connect(effectsNode);
        effectsNode.connect(destination);
    }
    else {
        voiceGain.connect(destination);
    }
    // Create ADSR envelope
    const envelope = new ADSREnvelope(audioContext, voiceGain);
    // Store active nodes for cleanup
    let activeOscillators: OscillatorNode[] = [];
    const voice: Voice = {
        gainNode: voiceGain,
        effectsNode: effectsNode || null,
        filterNode: null,
        trigger(_note: string, time: number, duration: number): void {
            // Coin sound: quick arpeggio of frequencies (ascending)
            // Define arpeggio frequencies (major triad going up)
            const baseFreq = 800; // Base frequency
            const frequencies = [
                baseFreq, // Root
                baseFreq * 1.25, // Major third
                baseFreq * 1.5, // Perfect fifth
                baseFreq * 2.0, // Octave
            ];
            // Time between each note in the arpeggio
            const noteInterval = 0.04; // 40ms between notes
            frequencies.forEach((freq, index) => {
                const noteTime = time + (index * noteInterval);
                const noteDuration = 0.08; // Each note is short
                // Create oscillator for this note
                const oscillator = audioContext.createOscillator();
                oscillator.type = 'sine'; // Sine wave for clean, bell-like tone
                oscillator.frequency.value = freq;
                // Create individual gain for this note (fade out as we go up)
                const noteGain = audioContext.createGain();
                const amplitude = 1.0 - (index * 0.15); // Slightly decrease volume for higher notes
                noteGain.gain.setValueAtTime(amplitude, noteTime);
                noteGain.gain.exponentialRampToValueAtTime(0.01, noteTime + noteDuration);
                // Connect: oscillator -> noteGain -> envelope
                oscillator.connect(noteGain);
                noteGain.connect(envelope.getGainNode());
                // Start and stop oscillator
                oscillator.start(noteTime);
                oscillator.stop(noteTime + noteDuration);
                // Store for cleanup
                activeOscillators.push(oscillator);
                // Clean up after oscillator stops
                oscillator.onended = () => {
                    try {
                        oscillator.disconnect();
                        noteGain.disconnect();
                    }
                    catch (e) {
                        // Ignore errors
                    }
                    activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
                };
            });
            // Trigger envelope for the overall sound
            envelope.trigger(time, duration, adsrSettings);
        },
        updateParameters(newParameters: InstrumentParameters): void {
            Object.assign({}, newParameters);
        },
        dispose(): void {
            activeOscillators.forEach(osc => {
                try {
                    osc.stop();
                    osc.disconnect();
                }
                catch (e) { /* ignore */ }
            });
            activeOscillators = [];
            envelope.dispose();
            try {
                voiceGain.disconnect();
            }
            catch (e) { /* ignore */ }
        },
    };
    return voice;
}
/**
 * Factory function to create native Web Audio API voices
 *
 * @param audioContext - Native AudioContext
 * @param instrumentTypes - Array of instrument types to create
 * @param adsrSettings - Optional array of ADSR settings per voice
 * @param instrumentParameters - Optional array of instrument parameters per voice
 * @param parametersEnabled - Optional array indicating if parameters are enabled per voice
 * @param effectsNodes - Optional array of AudioWorkletNodes for effects per voice
 * @param destination - Optional destination node (defaults to audioContext.destination)
 * @returns Array of Voice instances
 */
export function createNativeVoices(audioContext: AudioContext, instrumentTypes: InstrumentType[], adsrSettings?: ADSRSettings[], instrumentParameters?: (InstrumentParameters | undefined)[], _parametersEnabled?: boolean[], effectsNodes?: (AudioWorkletNode | null)[], destination?: AudioNode): Voice[] {
    // Use provided destination or create master gain node as destination
    const masterGain = destination || audioContext.destination;
    const voices: Voice[] = [];
    for (let i = 0; i < instrumentTypes.length; i++) {
        const instrumentType = instrumentTypes[i];
        if (!instrumentType) {
            const fallbackConfig: VoiceConfig = {
                audioContext,
                instrumentType: 'triangle',
                adsr: adsrSettings?.[i],
                parameters: instrumentParameters?.[i],
                effectsNode: effectsNodes?.[i] || null,
                destination: masterGain,
            };
            voices.push(createOscillatorVoice(fallbackConfig));
            continue;
        }
        const adsr = adsrSettings?.[i];
        const parameters = instrumentParameters?.[i];
        const effectsNode = effectsNodes?.[i] || null;
        const config: VoiceConfig = {
            audioContext,
            instrumentType,
            adsr,
            parameters,
            effectsNode,
            destination: masterGain,
        };
        // Create voices based on instrument type
        const basicOscillatorTypes = ['square', 'triangle', 'sawtooth', 'sine'];
        const pulseWaveTypes = ['pulse25', 'pulse50', 'pulse75'];
        const noiseTypes = ['noise-white', 'noise-pink', 'noise-brown'];
        const fmTypes = ['fmsynth'];
        const amTypes = ['amsynth'];
        const padTypes = ['pad-choir', 'pad-sweep', 'pad-drone'];
        const arpTypes = ['arp-fast', 'arp-slow', 'arp-random', 'arp-chord'];
        if (basicOscillatorTypes.includes(instrumentType)) {
            voices.push(createOscillatorVoice(config));
        }
        else if (pulseWaveTypes.includes(instrumentType)) {
            voices.push(createPulseVoice(config));
        }
        else if (noiseTypes.includes(instrumentType)) {
            voices.push(createNoiseVoice(config));
        }
        else if (fmTypes.includes(instrumentType)) {
            voices.push(createFMVoice(config));
        }
        else if (amTypes.includes(instrumentType)) {
            voices.push(createAMVoice(config));
        }
        else if (padTypes.includes(instrumentType)) {
            voices.push(createPadVoice(config));
        }
        else if (arpTypes.includes(instrumentType)) {
            voices.push(createArpVoice(config));
        }
        else if (instrumentType === 'drum-kick') {
            voices.push(createKickDrumVoice(config));
        }
        else if (instrumentType === 'drum-snare') {
            voices.push(createSnareDrumVoice(config));
        }
        else if (instrumentType === 'drum-hat') {
            voices.push(createHiHatVoice(config, false));
        }
        else if (instrumentType === 'drum-hat-open') {
            voices.push(createHiHatVoice(config, true));
        }
        else if (instrumentType === 'drum-tom' || instrumentType === 'drum-tom-mid') {
            voices.push(createTomDrumVoice(config, 'mid'));
        }
        else if (instrumentType === 'drum-tom-low') {
            voices.push(createTomDrumVoice(config, 'low'));
        }
        else if (instrumentType === 'drum-tom-high') {
            voices.push(createTomDrumVoice(config, 'high'));
        }
        else if (instrumentType === 'drum-cymbal-crash') {
            voices.push(createCymbalVoice(config, 'crash'));
        }
        else if (instrumentType === 'drum-cymbal-ride') {
            voices.push(createCymbalVoice(config, 'ride'));
        }
        else if (instrumentType === 'drum-clap') {
            voices.push(createClapVoice(config));
        }
        else if ((instrumentType as string) === 'pluck') {
            voices.push(createPluckVoice(config));
        }
        else if (instrumentType === 'metal') {
            voices.push(createMetalVoice(config));
        }
        else if (instrumentType === 'sfx-laser') {
            voices.push(createLaserVoice(config));
        }
        else if (instrumentType === 'sfx-explosion') {
            voices.push(createExplosionVoice(config));
        }
        else if (instrumentType === 'sfx-coin') {
            voices.push(createCoinVoice(config));
        }
        else {
            const fallbackConfig: VoiceConfig = {
                audioContext,
                instrumentType: 'triangle',
                adsr,
                parameters,
                effectsNode,
                destination: masterGain,
            };
            voices.push(createOscillatorVoice(fallbackConfig));
        }
    }
    return voices;
}
